import React, { useState, useContext, useEffect } from "react";
import {
  Button,
  Paper,
  Typography,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Table,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { getToken } from "../../../api/Account";
import { DataContext } from "../../../context/DataContext";

import { getRuta, simpleMessage } from "../../../helpers/Helpers";
import { tienePermiso } from "../../../helpers/permisos";
import { formatearMonto } from "../../../helpers/formato";
import {
  CargandoTabla,
  EstadoChip,
  EstadoError,
  EstadoVacio,
} from "../../../components/base";
import { useNavigate } from "react-router-dom";

import { getCreditsByClientAsync } from "../../../api/Clients";
import {
  celdaFecha,
  celdaMonto,
  encabezadoTabla,
  estadoDeCredito,
  fechaDeCredito,
} from "../../credits/comunes";
import { ERROR_OTRA_SESION } from "../comunes";

const FILAS_POR_PAGINA = 10;

const ClientCredits = ({ id, onCargado }) => {
  const token = getToken();
  let ruta = getRuta();
  let navigate = useNavigate();
  const [creditList, setCreditList] = useState([]);

  const { setIsLoading, setIsUnautorized, access } = useContext(DataContext);

  // Estado local de la pestaña: esqueleto mientras carga y error con "Reintentar".
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [pagina, setPagina] = useState(0);

  const cargar = async () => {
    setCargando(true);
    setError(false);
    setIsLoading(true);
    try {
      const result = await getCreditsByClientAsync(token, id);
      if (!result.statusResponse) {
        setIsLoading(false);
        setCargando(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        setError(true);
        if (result.error === ERROR_OTRA_SESION) {
          simpleMessage(result.error, "error");
        }
        return;
      }
      setIsLoading(false);
      setCreditList(result.data.result);
      setCargando(false);
      // El resumen del encabezado muestra la cantidad de créditos con lo que ya se cargó aquí.
      if (onCargado) onCargado(result.data.result.length);
    } catch (e) {
      // Sin red, `errorResponse` lanza una excepción: ni esqueleto eterno ni overlay encendido.
      setIsLoading(false);
      setCargando(false);
      setError("conexion");
    }
  };

  useEffect(() => {
    cargar();
  }, []);

  const currentItem = creditList.slice(
    pagina * FILAS_POR_PAGINA,
    (pagina + 1) * FILAS_POR_PAGINA
  );

  // "Ver crédito" abre GetCreditById (CREDITS VER); "Nuevo crédito" es AddCredit (CREDITS CREATE).
  const puedeAgregar = tienePermiso(access, "CREDITS CREATE");
  const puedeVer = tienePermiso(access, "CREDITS VER");

  const botonAgregar = (
    <Button
      onClick={() => {
        navigate(`${ruta}/credit/add/${id}`);
      }}
      variant="contained"
      startIcon={<AddIcon />}
    >
      Nuevo crédito
    </Button>
  );

  let contenido;
  if (cargando) {
    contenido = <CargandoTabla filas={4} columnas={5} etiqueta="Cargando los créditos…" />;
  } else if (error) {
    contenido = (
      <EstadoError
        titulo={
          error === "conexion"
            ? "No se pudo conectar con el servidor"
            : "No se pudieron cargar los créditos"
        }
        onReintentar={cargar}
      />
    );
  } else if (creditList.length === 0) {
    contenido = (
      <EstadoVacio
        titulo="Sin créditos"
        descripcion="Este cliente todavía no tiene créditos entregados."
        accion={puedeAgregar ? botonAgregar : undefined}
      />
    );
  } else {
    contenido = (
      <>
        <TableContainer>
          <Table aria-label="Créditos del cliente" size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={encabezadoTabla}>Crédito</TableCell>
                <TableCell sx={encabezadoTabla}>Estado</TableCell>
                <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>
                  Monto entregado
                </TableCell>
                <TableCell
                  sx={{
                    ...encabezadoTabla,
                    textAlign: "right",
                    display: { xs: "none", sm: "table-cell" },
                  }}
                >
                  Cuota
                </TableCell>
                <TableCell
                  sx={{ ...encabezadoTabla, display: { xs: "none", sm: "table-cell" } }}
                >
                  Vencimiento
                </TableCell>
                {puedeVer ? (
                  <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>
                    Acciones
                  </TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentItem.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell component="th" scope="row" sx={celdaFecha}>
                    {`#${row.id}`}
                  </TableCell>
                  <TableCell>
                    <EstadoChip {...estadoDeCredito(row)} />
                  </TableCell>
                  <TableCell sx={celdaMonto}>{formatearMonto(row.montoEntregado)}</TableCell>
                  <TableCell sx={{ ...celdaMonto, display: { xs: "none", sm: "table-cell" } }}>
                    {formatearMonto(row.cuotaPagar)}
                  </TableCell>
                  <TableCell sx={{ ...celdaFecha, display: { xs: "none", sm: "table-cell" } }}>
                    {fechaDeCredito(row.fechaVencimiento)}
                  </TableCell>
                  {puedeVer ? (
                    <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <Tooltip title="Ver crédito">
                        <IconButton
                          aria-label={`Ver el crédito #${row.id}`}
                          onClick={() => {
                            navigate(`${ruta}/credit/${row.id}`);
                          }}
                          sx={{ color: "primary.main" }}
                        >
                          <VisibilityOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={creditList.length}
          page={pagina}
          onPageChange={(e, nueva) => setPagina(nueva)}
          rowsPerPage={FILAS_POR_PAGINA}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          getItemAriaLabel={(tipo) =>
            ({
              first: "Primera página",
              last: "Última página",
              next: "Página siguiente",
              previous: "Página anterior",
            }[tipo])
          }
        />
      </>
    );
  }

  return (
    <div>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent={"space-between"}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Typography
            component="h2"
            variant="h6"
            sx={{ fontWeight: 700, color: "primary.main" }}
          >
            Créditos del cliente
          </Typography>

          {puedeAgregar ? botonAgregar : <></>}
        </Stack>

        {contenido}
      </Paper>
    </div>
  );
};

export default ClientCredits;
